import { useColorModeValue } from "@chakra-ui/react";
import Linkify from "components/DataDisplay/Linkify";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import UserTag, { UserTagProps } from "components/Tag/UserTag";
import { NewHireJourney, NewHireJourneyId } from "models/v2/newHireJourney";
import { User } from "features/User/models";
import { roleStyles, roleUrl, roleUser } from "./helpers";

interface InternalRoleTagProps extends Omit<UserTagProps, "user" | "name" | "imageUrl" | "role" | "isLoading"> {
  newHireJourney: NewHireJourney;
  roleName: "manager" | "buddy" | "people_team_contact" | "recruiter" | "it_team_contact" | "new_hire";
  includeLink?: boolean;
  includeRoleName?: boolean;
}

function RoleTag({ newHireJourney, roleName, includeLink, includeRoleName = false, ...rest }: InternalRoleTagProps) {
  const colorStyles = roleStyles(roleName);
  const bgColor = useColorModeValue(colorStyles.light, colorStyles.dark);
  const bgHover = useColorModeValue(colorStyles.lightHover, colorStyles.darkHover);
  const color = useColorModeValue(colorStyles.dark, colorStyles.light);
  const colorHover = useColorModeValue(colorStyles.darkHover, colorStyles.lightHover);
  const user = roleUser(newHireJourney, roleName);
  const maybeUrl = includeLink && user ? roleUrl(newHireJourney, roleName) : "";
  return (
    <Linkify linkTo={maybeUrl} _hover={{ textDecoration: "none" }} style={{ width: "100%" }}>
      <UserTag
        isLoading={!!newHireJourney}
        user={user}
        role={roleName}
        tagLabelProps={{ color, _hover: maybeUrl ? { color: colorHover } : {} }}
        bgColor={bgColor}
        _hover={maybeUrl ? { bgColor: bgHover } : {}}
        size="sm"
        includeRoleName={includeRoleName}
        {...rest}
      />
    </Linkify>
  );
}

interface NewHireTagByIdProps extends Omit<UserTagProps, "user" | "name" | "imageUrl" | "role"> {
  newHireJourneyId: NewHireJourneyId;
  includeLink?: boolean;
}

export function NewHireTagById({ newHireJourneyId, includeLink = true, ...rest }: NewHireTagByIdProps) {
  const { data: newHireJourney } = useNewHireJourneyDetailQuery(newHireJourneyId);
  return newHireJourney ? (
    <RoleTag newHireJourney={newHireJourney} roleName="new_hire" includeLink={includeLink} {...rest} />
  ) : null;
}

interface RoleTagProps extends Omit<UserTagProps, "user" | "name" | "imageUrl" | "role"> {
  newHireJourney: NewHireJourney;
  includeLink?: boolean;
}

export function NewHireTag({ newHireJourney, includeLink = true, ...rest }: RoleTagProps) {
  return <RoleTag newHireJourney={newHireJourney} roleName="new_hire" includeLink={includeLink} {...rest} />;
}

export function ManagerTag({ newHireJourney, includeLink = true, ...rest }: RoleTagProps) {
  return <RoleTag newHireJourney={newHireJourney} roleName="manager" includeLink={includeLink} {...rest} />;
}

export function PeopleTeamContactTag({ newHireJourney, includeLink = true, ...rest }: RoleTagProps) {
  return <RoleTag newHireJourney={newHireJourney} roleName="people_team_contact" includeLink={includeLink} {...rest} />;
}

export function BuddyTag({ newHireJourney, includeLink = true, ...rest }: RoleTagProps) {
  return <RoleTag newHireJourney={newHireJourney} roleName="buddy" includeLink={includeLink} {...rest} />;
}

export function RecruiterTag({ newHireJourney, includeLink = true, ...rest }: RoleTagProps) {
  return <RoleTag newHireJourney={newHireJourney} roleName="recruiter" includeLink={includeLink} {...rest} />;
}

export function ItTeamContactTag({ newHireJourney, includeLink = true, ...rest }: RoleTagProps) {
  return <RoleTag newHireJourney={newHireJourney} roleName="it_team_contact" includeLink={includeLink} {...rest} />;
}

interface UserTagWithRoleProps {
  user: User | undefined;
  roleName: "manager" | "buddy" | "people_team_contact" | "recruiter" | "it_team_contact" | "new_hire" | undefined;
  includeLink?: boolean;
}

export function UserTagWithRole({ user, roleName, includeLink = false, ...rest }: UserTagWithRoleProps) {
  const colorStyles = roleStyles(roleName);
  const bgColor = useColorModeValue(colorStyles.light, colorStyles.dark);
  const color = useColorModeValue(colorStyles.dark, colorStyles.light);
  return (
    <UserTag
      isLoading={!!user}
      user={user}
      role={roleName}
      tagLabelProps={{ color }}
      bgColor={bgColor}
      size="sm"
      {...rest}
    />
  );
}
